'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Check } from 'lucide-react';

const personas = [
  {
    id: 'sales',
    label: 'Sales leaders',
    headline: 'Forecast without the forecast meeting.',
    description: 'Tessa phones each rep on your cadence, asks about pipeline, blockers, and commitments, and sends you one digest before your week starts.',
    useCase: 'Friday 4pm: Tessa calls each rep for about ten minutes — pipeline movement, stuck deals, commitments for next week. Monday 9am: you read one digest instead of running a forecast meeting.',
    features: [
      '4 questions · ~10 minutes per rep',
      'Phone-first — reps answer from the road, no laptop',
      'Blockers and commitments in one digest',
      "Reply to the digest to steer next week's questions",
    ],
    cta: 'Start a sales check-in',
    href: '/routines/new/weekly-sales-checkin',
  },
  {
    id: 'engineering',
    label: 'Engineering leads',
    headline: "Standups that don't interrupt deep work.",
    description: 'Tessa runs the standup over Slack or email, on each engineer\'s own time. Answers roll into one digest — no 9:15 interruption.',
    useCase: 'Tessa pings each engineer on Slack or email with your questions; they answer whenever there\'s a natural break in their work. You get one digest with blockers on top — and no meeting happened.',
    features: [
      'Async over Slack or email — no meeting to attend',
      'Members never need an account or an app',
      'Full answers kept, 30-second summary on top',
      'Blockers surface before they stall the sprint',
    ],
    cta: 'Start a dev standup',
    // ?channel=calls preselects the wizard's Calls-first reach card — the
    // dev-standup funnel is steered calls-first (same as /for-dev-teams'
    // CTA). Other personas keep their template's own default.
    href: '/routines/new/dev-team-standup?channel=calls',
  },
  {
    id: 'field',
    label: 'Field & ops managers',
    headline: "Your crew doesn't sit at desks. Tessa calls them.",
    description: 'No laptop, no app, no forms. Tessa phones each crew member, asks your questions, and you read what came back.',
    useCase: 'Tessa texts a heads-up before her first call, then phones each crew member on your cadence — no login, nothing to install. Issues and blockers from the field land in your digest the same day.',
    features: [
      'Phone-first — built for crews without desks',
      'SMS heads-up before the first call',
      'Anyone can say SKIP; Tessa respects it',
      '4 questions · ~5 minutes per person',
    ],
    cta: 'Start a crew check-in',
    href: '/routines/new/maintenance-crew-checkin',
  },
  {
    id: 'people',
    label: 'People & HR',
    headline: "Hear how people are doing before it's in their resignation letter.",
    description: 'A recurring pulse over email or Slack that people actually answer — with sentiment trends you can act on.',
    useCase: 'On your cadence, Tessa asks every person four questions over email or Slack. You see how answers trend across weeks instead of relying on one-off surveys — and members are free, so you can pulse the whole org.',
    features: [
      '4 questions · ~7 minutes per person',
      'Email or Slack — wherever people already are',
      'Trends across weeks, not one-off surveys',
      'Members are always free — pulse the whole org',
    ],
    cta: 'Start a satisfaction pulse',
    href: '/routines/new/employee-satisfaction-pulse',
  },
];

export default function PersonaTabsSection() {
  const [activeTab, setActiveTab] = useState('sales');
  const active = personas.find((p) => p.id === activeTab) || personas[0];

  return (
    <section className="py-20 bg-card border-t border-b border-border">
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="text-center mb-10">
          <div className="landing-section-label">Built for how your team works</div>
          <h2 className="text-[38px] font-bold tracking-[-1.5px] mb-3.5 text-foreground">
            Different teams, same platform
          </h2>
        </div>

        <div className="flex justify-center gap-2 mb-10 flex-wrap">
          {personas.map((p) => (
            <button
              key={p.id}
              onClick={() => setActiveTab(p.id)}
              className={`px-5 py-2.5 rounded-full text-[14px] font-medium transition-all ${
                activeTab === p.id
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground border border-border hover:border-foreground hover:text-foreground'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h3 className="text-[26px] font-bold tracking-[-0.5px] mb-3 text-foreground">
              {active.headline}
            </h3>
            <p className="text-[15px] text-muted-foreground leading-[1.7] mb-6">
              {active.description}
            </p>
            <ul className="space-y-2.5 mb-6">
              {active.features.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-[14px] text-foreground">
                  <Check className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <Link
              href={active.href}
              className="inline-flex items-center gap-2 bg-[#EF4444] text-white px-6 py-3 rounded-full text-[14px] font-semibold hover:bg-[#DC2626] transition-all"
            >
              {active.cta}
            </Link>
          </div>
          <div className="bg-muted border border-border rounded-[16px] p-7">
            <div className="text-[11px] uppercase tracking-[1px] text-muted-foreground mb-3 font-space-mono">
              How it runs
            </div>
            <p className="text-[14px] leading-[1.7] text-foreground">{active.useCase}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
